<template>
  <div class="privacy-policy-page">
    <div class="privacy-policy-page__inner">
      <header class="privacy-policy-header">
        <p class="privacy-policy-header__eyebrow">Legal</p>
        <h1>Clovyr.app Privacy Policy</h1>
        <p class="privacy-policy-header__updated">Last updated: Aug 6, 2026</p>

        <div class="privacy-policy-header__introduction">
          <p>
            Your privacy is important to Clovyr, and so is being transparent about our privacy
            practices. This Privacy Policy explains how Clovyr Co. ("Clovyr," "we," "us," or "our")
            collects, uses, stores, and shares information in connection with clovyr.app, the
            Clovyr.app browser-based platform, Clovyr-provided hosting, and applications developed
            and operated by Clovyr, including Clovyr AI Cloud (collectively, the "Services").
          </p>
          <p>
            This Privacy Policy does not govern the Clovyr corporate website at clovyr.io, which has
            a separate privacy policy. It also does not govern the independent data practices of
            third-party applications, cloud providers, or AI model providers that a user chooses to
            connect to or deploy through the Services, except to the extent Clovyr itself processes
            information as described here.
          </p>
        </div>

        <p class="privacy-policy-header__commitment">
          We designed Clovyr.app so that Clovyr does not possess the credentials needed to access
          customer servers and cannot read customer application data. We do not, and will not, sell
          personal information.
        </p>
      </header>

      <nav class="privacy-policy-contents" aria-label="Privacy policy sections">
        <h2>On this page</h2>
        <ol>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#how-clovyr-app-works' }"
              >How Clovyr.app Works</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#information-we-collect' }"
              >Information We Collect</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#credentials-and-customer-data' }"
              >Credentials and Customer Data</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#third-party-applications' }"
              >Third-Party Applications</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#clovyr-ai-cloud' }"
              >Clovyr AI Cloud</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#google-workspace-api-data' }"
              >Google Workspace API Data</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#how-we-use-information' }"
              >How We Use Information</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#how-we-share-information' }"
              >How We Share Information</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#data-retention-and-deletion' }"
              >Data Retention and Deletion</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#security' }">Security</RouterLink>
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#your-choices-and-rights' }"
              >Your Choices and Rights</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#third-party-services-and-links' }"
              >Third-Party Services and Links</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#childrens-privacy' }"
              >Children's Privacy</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#international-users' }"
              >International Users</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#changes-to-this-policy' }"
              >Changes to This Privacy Policy</RouterLink
            >
          </li>
          <li>
            <RouterLink :to="{ path: '/privacy-policy', hash: '#contact-information' }"
              >Contact Information</RouterLink
            >
          </li>
        </ol>
      </nav>

      <article class="privacy-policy-document">
        <section id="how-clovyr-app-works">
          <h2><span>1.</span> How Clovyr.app Works</h2>
          <p>
            Clovyr.app operates as a browser-based control plane. Deployment and management
            instructions are generated and sent from the user's browser directly to the
            infrastructure and application selected by the user. Clovyr servers do not sit in the
            communication path between the user's browser, the user's cloud provider, the customer's
            server, or an application running on that server.
          </p>
          <p>
            When launching an application, a user may choose Clovyr-provided hosting or may bring an
            account with a supported cloud provider, such as Amazon Web Services, DigitalOcean, or
            Linode.
          </p>
          <p>
            For Clovyr-provided hosting, Clovyr provisions and hands off a server for the user's
            exclusive control. Clovyr systems and personnel do not retain administrative credentials
            that allow them to enter the server or inspect the applications and data on it.
          </p>
          <p>
            For bring-your-own-cloud deployments, the user supplies access credentials or completes
            authorization with the selected cloud provider. Those credentials are encrypted within
            the user's browser and are sent directly from the browser to the selected provider as
            needed. Clovyr servers do not receive those credentials in readable form and cannot use
            them.
          </p>
          <p>
            After a server is launched, the browser communicates directly with that server to deploy
            and maintain applications. Clovyr systems and personnel cannot access customer servers,
            applications, or application data, including for troubleshooting or debugging.
          </p>
        </section>

        <section id="information-we-collect">
          <h2><span>2.</span> Information We Collect</h2>
          <p>
            We may collect a limited amount of information from or about users and their devices as
            described below.
          </p>

          <h3>Information You Provide</h3>
          <p>
            <strong>Account and communications information.</strong> If you create an account,
            purchase a Service, request support, or contact us, we may receive information such as
            your name, email address, organization, role, subscription or billing status, and the
            contents of messages or attachments you choose to send.
          </p>
          <p>
            <strong>Payment information.</strong> Payments may be processed by a payment service
            provider. Clovyr may receive transaction identifiers, subscription status, billing
            contact information, and limited payment-related metadata, but does not need to receive
            complete payment-card credentials from the payment processor.
          </p>

          <h3>Information Collected When You Use Clovyr.app</h3>
          <p>
            <strong>Device and request information.</strong> The systems that deliver clovyr.app may
            receive standard technical information such as IP address, browser type, operating
            system, request date and time, and security or error information.
          </p>
          <p>
            <strong>Usage information.</strong> We may receive limited information about
            interactions with the Clovyr.app interface to operate, secure, and improve the Services.
            This does not include the contents of customer servers, applications, documents,
            conversations, prompts, model responses, or cloud credentials.
          </p>
          <p>
            <strong>Cookies and local browser storage.</strong> We may use cookies or browser
            storage for authentication, preferences, security, and core functionality. Some
            essential browser storage is necessary for the Services to function.
          </p>

          <h3>Deployment and Hosting Information</h3>
          <p>
            To provide an account, subscription, or hosting service, Clovyr may maintain limited
            administrative metadata such as the selected plan, infrastructure region, server
            identifier, provisioning state, service status, timestamps, and encrypted backup or
            recovery objects. This metadata does not give Clovyr the ability to enter the server or
            read application data.
          </p>
        </section>

        <section id="credentials-and-customer-data">
          <h2><span>3.</span> Credentials, Encryption, and Customer-Controlled Data</h2>
          <p>
            Credentials and secrets used by Clovyr.app are kept in an encrypted, user-controlled
            vault. Encryption and decryption occur in the user's browser, and the keys required to
            decrypt the vault are held by the user. Clovyr does not receive those keys and cannot
            recover or decrypt the vault.
          </p>
          <p>
            Clovyr may store encrypted backups, archives, or opaque data blobs when that
            functionality is enabled. Clovyr cannot read their contents because it does not possess
            the user's decryption keys.
          </p>
          <p>
            Customer application data - including files, databases, messages, prompts, model
            responses, application credentials, and other content processed on a customer server -
            remains within the infrastructure and applications controlled by the user. Clovyr does
            not collect or have access to that data.
          </p>
          <p>
            Because Clovyr cannot access customer servers or decrypt customer vaults and backups,
            Clovyr personnel cannot inspect customer data for support, maintenance, troubleshooting,
            or debugging. Users are responsible for retaining their keys and maintaining access to
            their environments. If a user loses the only available decryption key, Clovyr may be
            unable to restore access.
          </p>
        </section>

        <section id="third-party-applications">
          <h2><span>4.</span> Third-Party Applications</h2>
          <p>
            Clovyr.app allows users to discover and launch applications developed by Clovyr and
            applications developed by independent third parties. Unless an application is expressly
            identified as developed or operated by Clovyr, Clovyr does not develop, control, or
            determine that application's data-handling practices and may have no relationship with
            its developer.
          </p>
          <p>
            This Privacy Policy governs information Clovyr processes in providing the Clovyr.app
            platform and Clovyr-provided hosting. It does not govern information that an
            independently developed application processes within the user's server or sends to
            services selected by the user.
          </p>
          <p>
            Users should review an application's documentation, privacy policy, license, and terms
            before launching it or connecting accounts. Launching an application through Clovyr.app
            does not mean that Clovyr endorses, audits, or assumes responsibility for that
            application's privacy or security practices.
          </p>
        </section>

        <section id="clovyr-ai-cloud">
          <h2><span>5.</span> Clovyr AI Cloud</h2>
          <p>
            Clovyr AI Cloud is developed by Clovyr and can be deployed through Clovyr.app into an
            environment controlled by the user. The application may process documents, indexes,
            embeddings, conversations, prompts, model responses, configuration information, and
            credentials within that environment. Clovyr servers and personnel do not receive or have
            access to that content.
          </p>
          <p>
            Users may configure Clovyr AI Cloud to use locally hosted models or third-party AI model
            services. When a user selects a third-party service, supplies the user's own
            credentials, and requests an operation, Clovyr AI Cloud may send the information
            necessary to perform that operation directly from the user's deployment to the provider
            selected by the user. The request does not pass through Clovyr servers, and Clovyr
            cannot see the user's provider credentials, prompts, documents, or responses.
          </p>
          <p>
            A user may instead select a self-hosted or offline model. In that configuration, model
            processing occurs within the user's own isolated environment and the information is not
            sent to the model's developer or an external model API.
          </p>
          <p>
            Third-party AI providers have their own privacy policies, contractual terms, retention
            practices, and model-training practices. Because users independently select and
            authenticate to those providers, users should confirm that the selected provider and
            service tier meet their privacy and compliance requirements before sending information
            to it. Clovyr does not authorize any provider acting on Clovyr's behalf to use customer
            data to create, train, or improve a generalized or foundational AI or machine-learning
            model.
          </p>
        </section>

        <section id="google-workspace-api-data">
          <h2><span>6.</span> Google Workspace API Data</h2>
          <p>
            A user may choose to connect Clovyr AI Cloud to Google Workspace. With the user's
            authorization, the application may access user-selected Google Drive files, related file
            metadata, and supported document content, including Google Docs, Sheets, and Slides,
            solely to provide the document search, retrieval, indexing, summarization,
            question-answering, and other user-facing features requested by the user.
          </p>
          <p>
            Google authorization credentials and Google Workspace data are handled within the user's
            browser and user-controlled Clovyr AI Cloud deployment. They are not transmitted to or
            accessible by Clovyr servers or personnel. Any copies, indexes, or derived
            representations created by Clovyr AI Cloud are stored in the user's environment and
            remain under the user's control.
          </p>
          <p>
            If a user explicitly configures a third-party AI provider with user-owned credentials,
            selected Google Workspace content may be sent directly from the user's deployment to
            that provider only as necessary to perform the user-requested feature. If the user
            selects a self-hosted or offline model, the content remains within the user's
            environment and is not shared with the model provider.
          </p>
          <p>
            Clovyr AI Cloud's use of information received from Google Workspace APIs will adhere to
            the
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              >Google API Services User Data Policy</a
            >, including the
            <a
              href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy#limited-use"
              target="_blank"
              rel="noopener noreferrer"
              >Limited Use requirements</a
            >.
          </p>
          <p>
            Clovyr does not access, use, transfer, sell, or permit services acting on its behalf to
            use Google Workspace API data - including raw, aggregated, anonymized, or derived data -
            to create, train, or improve generalized or non-personalized artificial intelligence or
            machine-learning models. Clovyr does not use Google Workspace API data for advertising,
            retargeting, creditworthiness, or lending.
          </p>
          <p>
            Clovyr does not allow humans to read Google Workspace API data because Clovyr does not
            have access to it. A user controls access within the user's own deployment and may
            remove a Google connection, delete imported or derived data, or delete the deployment
            using the controls available in the applicable application and infrastructure.
          </p>
          <p>
            Clovyr AI Cloud requests access to Google Workspace data only when a user initiates the
            connection and grants the requested permissions. Users may revoke Clovyr AI Cloud's
            access through their Google Account settings and may remove the corresponding
            credentials and data from their Clovyr AI Cloud deployment.
          </p>
        </section>

        <section id="how-we-use-information">
          <h2><span>7.</span> How We Use Information We Collect</h2>
          <p>We use the limited information Clovyr receives:</p>
          <ul>
            <li>
              To provide, maintain, secure, and improve Clovyr.app and Clovyr-provided hosting;
            </li>
            <li>To create and administer accounts, subscriptions, and transactions;</li>
            <li>To provision infrastructure and display service status;</li>
            <li>
              To communicate with users, respond to requests, and provide customer support that does
              not require access to customer servers or application data;
            </li>
            <li>To detect fraud, abuse, security incidents, and technical failures;</li>
            <li>To comply with applicable law and enforce our legal rights; and</li>
            <li>
              For another purpose disclosed when the information is collected and, where required,
              with the user's consent.
            </li>
          </ul>
          <p>
            We may create aggregated or de-identified information from platform information that
            Clovyr lawfully receives. We do not create aggregated or derived datasets from customer
            application data or Google Workspace API data because Clovyr does not have access to
            that data.
          </p>
        </section>

        <section id="how-we-share-information">
          <h2><span>8.</span> How We Share Information</h2>
          <p>
            <strong>Vendors and service providers.</strong> We may share limited account, payment,
            website-delivery, communications, security, and infrastructure-administration
            information with vendors that help us provide the Services. These vendors are permitted
            to process the information only for the services they provide to Clovyr and subject to
            appropriate obligations.
          </p>
          <p>
            <strong>User-directed providers.</strong> A user's browser or customer-controlled
            application may communicate directly with a cloud provider, third-party application,
            identity provider, data source, or AI model provider selected by the user. Those direct
            communications do not pass through Clovyr systems. The selected provider's terms and
            privacy practices apply.
          </p>
          <p>
            <strong>Legal and safety disclosures.</strong> We may access, preserve, or disclose
            information that Clovyr possesses if we reasonably believe disclosure is required to
            comply with law or legal process, protect rights or safety, investigate abuse, or secure
            the Services. This does not expand our technical ability to access encrypted or
            customer-controlled data.
          </p>
          <p>
            <strong>Corporate transactions.</strong> Information Clovyr possesses may be disclosed
            to service providers, advisers, and transactional parties in connection with a merger,
            acquisition, financing, reorganization, or sale of assets, subject to applicable law and
            required consent.
          </p>
          <p>
            <strong>Consent.</strong> We may share information with a third party at the user's
            direction or with the user's permission.
          </p>
          <p><strong>Clovyr does not sell personal information.</strong></p>
        </section>

        <section id="data-retention-and-deletion">
          <h2><span>9.</span> Data Retention and Deletion</h2>
          <p>
            Clovyr retains account, billing, communications, security, and operational information
            only for as long as reasonably necessary to provide the Services, comply with legal
            obligations, resolve disputes, enforce agreements, and protect the Services.
          </p>
          <p>
            Customer application data is retained within the user's server and applications
            according to the settings and retention choices controlled by the user. Clovyr cannot
            independently view or delete that data.
          </p>
          <p>
            Encrypted backups or opaque blobs stored by Clovyr are retained only while needed to
            provide the enabled backup or recovery feature, or as otherwise required by law. Users
            may delete supported backups or terminate the associated Service through the available
            controls. Because Clovyr lacks the decryption keys, retained encrypted objects remain
            unreadable to Clovyr.
          </p>
          <p>
            Users may delete data inside their applications, disconnect third-party services, revoke
            Google authorization, delete deployments, or close their Clovyr account using the
            available controls. Users may also submit an account or data-deletion request through
            the contact method below. We may retain limited records when required by law or for
            legitimate security, fraud-prevention, accounting, or dispute-resolution purposes.
          </p>
        </section>

        <section id="security">
          <h2><span>10.</span> Security</h2>
          <p>
            Clovyr uses technical and organizational safeguards designed to protect information it
            maintains. The Clovyr.app architecture limits Clovyr's access by keeping credential
            encryption and decryption in the browser, routing management communications directly
            between the browser and user-selected infrastructure, and placing customer applications
            and data under user control.
          </p>
          <p>
            No method of electronic transmission or storage is entirely secure. Users are
            responsible for protecting their devices, vault keys, cloud accounts, deployed servers,
            applications, and third-party credentials. Clovyr cannot reset or recover user-held
            encryption keys that it does not possess.
          </p>
        </section>

        <section id="your-choices-and-rights">
          <h2><span>11.</span> Your Choices and Rights</h2>
          <p>
            Users may manage browser settings, disconnect integrations, revoke third-party
            authorizations, delete deployments, and control information stored inside their own
            applications. Marketing communications may be unsubscribed from using the link included
            in the message, although administrative or service-related communications may still be
            sent.
          </p>
          <p>
            Depending on location, users may have rights to request access to, correction of, or
            deletion of personal information that Clovyr maintains. Clovyr can respond only with
            respect to information it possesses; it cannot retrieve readable information from
            encrypted vaults, encrypted backups, or customer-controlled servers.
          </p>
        </section>

        <section id="third-party-services-and-links">
          <h2><span>12.</span> Third-Party Services and Links</h2>
          <p>
            The Services may contain links to or interoperate with websites, cloud providers,
            applications, identity providers, and model providers that Clovyr does not own or
            operate. Clovyr is not responsible for their privacy practices. Users should review
            their policies before providing information or credentials.
          </p>
        </section>

        <section id="childrens-privacy">
          <h2><span>13.</span> Children's Privacy</h2>
          <p>
            The Services are not directed to children under 13, and we do not knowingly collect
            personal information from children under 13. If you believe a child has provided Clovyr
            with personal information in violation of this Policy, please contact us.
          </p>
        </section>

        <section id="international-users">
          <h2><span>14.</span> International Users</h2>
          <p>
            Clovyr is based in the United States. Information that Clovyr receives may be processed
            in the United States and other locations where Clovyr or its service providers operate.
            Customer application data remains in the infrastructure and region selected or
            controlled by the user, subject to the practices of the selected infrastructure and
            application providers.
          </p>
        </section>

        <section id="changes-to-this-policy">
          <h2><span>15.</span> Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the revised version at
            clovyr.app and update the date above. If we materially change how we use or share
            personal information previously collected, we will provide notice through the Services,
            by email, or by another appropriate method and obtain consent when required.
          </p>
        </section>

        <section id="contact-information">
          <h2><span>16.</span> Contact Information</h2>
          <p>
            Questions, concerns, or requests concerning this Privacy Policy or Clovyr's processing
            practices may be submitted through
            <a href="https://clovyr.io/contact" target="_blank" rel="noopener noreferrer"
              >Clovyr's contact page</a
            >
            or by writing to:
          </p>
          <address>
            Clovyr Co.<br />
            228 Park Ave S., Suite 60793<br />
            New York, NY 10003
          </address>
        </section>
      </article>
    </div>
  </div>
</template>

<style scoped lang="scss">
.privacy-policy-page {
  min-height: 100vh;
  padding: clamp(32px, 6vw, 80px) clamp(20px, 5vw, 72px) 96px;
  color: #ded9e8;
  background: radial-gradient(circle at 85% 4%, rgb(92 68 127 / 24%), transparent 30rem), #221a33;
  font-family: 'Work Sans', sans-serif;
}

.privacy-policy-page__inner {
  width: min(100%, 920px);
  margin: 0 auto;
}

.privacy-policy-header {
  padding: clamp(28px, 5vw, 56px);
  background: linear-gradient(145deg, rgb(65 51 88 / 88%), rgb(47 36 66 / 96%));
  border: 1px solid rgb(255 255 255 / 9%);
  border-radius: 20px;
  box-shadow: 0 28px 70px rgb(10 5 22 / 24%);
}

.privacy-policy-header__eyebrow {
  margin: 0 0 14px;
  color: #53d7a0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.privacy-policy-header h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(36px, 6vw, 58px);
  font-weight: 650;
  letter-spacing: -0.045em;
  line-height: 1.04;
}

.privacy-policy-header__updated {
  margin: 18px 0 0;
  color: #aea5bd;
  font-size: 14px;
}

.privacy-policy-header__introduction {
  margin-top: 34px;
  font-size: 17px;
  line-height: 1.75;
}

.privacy-policy-header__introduction p:last-child {
  margin-bottom: 0;
}

.privacy-policy-header__commitment {
  margin: 30px 0 0;
  padding: 20px 22px;
  color: #f4f1f7;
  font-size: 16px;
  font-weight: 550;
  line-height: 1.65;
  background: rgb(0 191 116 / 10%);
  border: 1px solid rgb(83 215 160 / 28%);
  border-left: 3px solid #53d7a0;
  border-radius: 4px 12px 12px 4px;
}

.privacy-policy-contents {
  margin: 40px 0;
  padding: 28px clamp(24px, 5vw, 40px);
  background: rgb(30 22 47 / 70%);
  border: 1px solid rgb(255 255 255 / 7%);
  border-radius: 16px;
}

.privacy-policy-contents h2 {
  margin: 0 0 20px;
  color: #fff;
  font-size: 18px;
  font-weight: 650;
}

.privacy-policy-contents ol {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 36px;
  margin: 0;
  padding-left: 22px;
  color: #7f748e;
  font-size: 14px;
}

.privacy-policy-contents a,
.privacy-policy-document a {
  color: #70dcb0;
  text-decoration-color: rgb(112 220 176 / 36%);
  text-underline-offset: 3px;
  transition:
    color 160ms ease,
    text-decoration-color 160ms ease;
}

.privacy-policy-contents a:hover,
.privacy-policy-document a:hover {
  color: #a4edcf;
  text-decoration-color: currentcolor;
}

.privacy-policy-document {
  padding: 0 clamp(4px, 3vw, 24px);
}

.privacy-policy-document section {
  padding: 42px 0;
  scroll-margin-top: 24px;
  border-top: 1px solid rgb(255 255 255 / 9%);
}

.privacy-policy-document section:first-child {
  border-top: 0;
}

.privacy-policy-document h2 {
  margin: 0 0 24px;
  color: #fff;
  font-size: clamp(24px, 3.5vw, 30px);
  font-weight: 620;
  letter-spacing: -0.025em;
  line-height: 1.24;
}

.privacy-policy-document h2 span {
  color: #53d7a0;
}

.privacy-policy-document h3 {
  margin: 34px 0 14px;
  color: #f4f1f7;
  font-size: 18px;
  font-weight: 650;
  line-height: 1.4;
}

.privacy-policy-document p,
.privacy-policy-document li,
.privacy-policy-document address {
  color: #c5bece;
  font-size: 16px;
  line-height: 1.75;
}

.privacy-policy-document p {
  margin: 0 0 18px;
}

.privacy-policy-document p:last-child {
  margin-bottom: 0;
}

.privacy-policy-document strong {
  color: #eeeaf3;
  font-weight: 650;
}

.privacy-policy-document ul {
  margin: 8px 0 24px;
  padding-left: 24px;
}

.privacy-policy-document li {
  padding-left: 6px;
  margin-bottom: 8px;
}

.privacy-policy-document li::marker {
  color: #53d7a0;
}

.privacy-policy-document address {
  display: inline-block;
  margin-top: 6px;
  padding: 18px 22px;
  color: #eeeaf3;
  font-style: normal;
  background: rgb(255 255 255 / 4%);
  border-radius: 10px;
}

@media (max-width: 700px) {
  .privacy-policy-page {
    padding-right: 16px;
    padding-bottom: 64px;
    padding-left: 16px;
  }

  .privacy-policy-header {
    border-radius: 14px;
  }

  .privacy-policy-header__introduction {
    font-size: 16px;
  }

  .privacy-policy-contents ol {
    grid-template-columns: 1fr;
  }

  .privacy-policy-document section {
    padding: 34px 0;
  }
}
</style>
